/**
 * Shared cohort constants for Power Curve tiers.
 * Used by: Portfolio Power Curve panel, Scenario Modeling, Campaign Planning.
 */

/** Default cohort names mapped to fractal tiers (highest concentration first) */
export const DEFAULT_COHORT_NAMES = [
  "Anchor",
  "Strategic",
  "Key Account",
  "Core",
  "Long Tail",
] as const;

export type CohortName = (typeof DEFAULT_COHORT_NAMES)[number];

/** Tailwind badge classes per cohort */
export const COHORT_COLORS: Record<string, string> = {
  Anchor: "bg-primary text-primary-foreground",
  Strategic: "bg-primary/80 text-primary-foreground",
  "Key Account": "bg-primary/60 text-primary-foreground",
  Core: "bg-primary/30 text-primary-foreground",
  "Long Tail": "bg-muted text-muted-foreground",
};

/**
 * Fallback acceptance rates (%) — used when neither a corporate override
 * (via corporate_financial_defaults table) nor a negotiation template is assigned.
 * Per-corporate overrides are loaded by financialDefaults.service.ts.
 *
 * @source unsourced — origin unknown as of 2026-08-08. Not derived from platform data:
 * there is no recorded derivation, no sample, and no date behind any of the five values.
 * They flow into scenario modelling and campaign planning, and from there toward an
 * "expected realized volume" figure, which is why they are LABELLED rather than deleted or
 * quietly "fixed": deleting them breaks planning for a demo that is weeks away, and
 * changing a number to make a claim publishable is the same category of error this labelling
 * exists to prevent.
 *
 * CONSEQUENCE, and it is mechanical rather than advisory: any proof claim whose derivation
 * touches these grades `provenance: default_unsourced`, which the publication gate refuses
 * to project to any external viewer role and requires a recorded assumption for even
 * internally. Sourcing them is R122 — derive from realised enrollment outcomes per cohort,
 * or obtain an industry figure with a citation, then update this annotation.
 *
 * The same applies to the 30% last-resort fallback documented in FORMULA_AUDIT.md and
 * asserted by scenario.service.test.ts.
 */
export const DEFAULT_ACCEPTANCE_RATES: Record<string, number> = {
  Anchor: 85,
  Strategic: 70,
  "Key Account": 60,
  Core: 45,
  "Long Tail": 25,
};

/** Map a tier index (0-based, highest leverage first) to a cohort name */
export function getCohortName(tierIndex: number, totalTiers: number): string {
  if (totalTiers <= DEFAULT_COHORT_NAMES.length) {
    const offset = DEFAULT_COHORT_NAMES.length - totalTiers;
    return DEFAULT_COHORT_NAMES[tierIndex + offset] ?? "Long Tail";
  }
  return tierIndex < DEFAULT_COHORT_NAMES.length
    ? DEFAULT_COHORT_NAMES[tierIndex]
    : "Long Tail";
}
